import { useState, useEffect } from 'react'
import { useAdminBusiness } from '../../contexts/AdminBusinessContext'
import { useSocket } from '../../contexts/SocketContext'
import { ordersAPI } from '../../services/api'
import { loadBusinessInfo as loadStoreBusinessInfo } from '../../utils/bill'
import { 
  MagnifyingGlassIcon, 
  QrCodeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  PrinterIcon,
  KeyIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import InvoiceRenderer from '../../components/InvoiceRender'
import PaymentProofReview, { PROOF_BADGES } from '../../components/PaymentProofReview'
import { t, locale } from '../../i18n'
import { useTheme } from '../../contexts/ThemeContext'

const PAYMENT_METHOD_LABELS = { cash: 'Cash', card: 'Card', bank_transfer: 'Bank transfer' }

function OrdersPage() {
  const { businessType } = useAdminBusiness()
  const isRestaurant = businessType === 'restaurant'
  const { socket, updateOrderStatus: socketUpdateOrderStatus } = useSocket()
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    status: '',
    table: '',
    search: ''
  })
  const [searchInput, setSearchInput] = useState('')
  const [showOrderModal, setShowOrderModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState('default')
  const [businessInfo, setBusinessInfo] = useState(null)
  const [loadingBusinessInfo, setLoadingBusinessInfo] = useState(true)
  const { getCurrency } = useTheme()

  // MySQL returns DECIMAL columns as strings, so always coerce before formatting.
  const money = value => `${Number(value || 0).toFixed(2)} ${businessInfo?.currency || getCurrency()}`

  useEffect(() => {
    loadOrders()
    loadBusinessInfo()
    
    // Check for URL parameters to show modal directly
    const orderSearchQuery = searchParams.get('orderSearchQuery')
    if (orderSearchQuery) {
      handleSearchByCodeOrPin(orderSearchQuery)
    }
  }, [])

  // Socket.io real-time updates
  useEffect(() => {
    if (socket) {
      // Listen for new orders
      socket.on('order.created', (newOrder) => {
        console.log('📝 New order created:', newOrder)
        setOrders(prev => [newOrder, ...prev])
        toast.success(t("New order {order_code} received!", { order_code: newOrder.order_code }))
      })

      // Listen for order updates
      socket.on('order.updated', (updatedOrder) => {
        console.log('🔄 Order updated:', updatedOrder)
        setOrders(prev => prev.map(order => 
          order.id === updatedOrder.id ? updatedOrder : order
        ))
        if (selectedOrder && selectedOrder.id === updatedOrder.id) {
          setSelectedOrder(updatedOrder)
        }
      })

      // Listen for payment updates
      socket.on('payment.recorded', (paymentData) => {
        console.log('💰 Payment recorded:', paymentData)
        setOrders(prev => prev.map(order => 
          order.id === paymentData.orderId 
            ? { ...order, payment_status: paymentData.paymentStatus }
            : order
        ))
      })

      // A customer uploaded a transfer receipt, or staff reviewed one.
      socket.on('order.payment_proof', (data) => {
        const patch = { payment_proof_status: data.payment_proof_status, ...(data.payment_status ? { payment_status: data.payment_status } : {}), payment_proof_uploaded_at: Date.now() }
        setOrders(prev => prev.map(order => order.id === data.orderId ? { ...order, ...patch } : order))
        setSelectedOrder(prev => prev && prev.id === data.orderId ? { ...prev, ...patch } : prev)
        if (data.payment_proof_status === 'pending') toast(t("Transfer receipt uploaded for {v0}", { v0: data.orderCode || 'an order' }), { icon: '🧾' })
      })

      return () => {
        socket.off('order.payment_proof')
        socket.off('order.created')
        socket.off('order.updated')
        socket.off('payment.recorded')
      }
    }
  }, [socket, selectedOrder])

  useEffect(() => {
    applyFilters()
  }, [orders, filters])

  const loadOrders = async () => {
    try {
      setLoading(true)
      const response = await ordersAPI.getOrders({ 
        branchId: 1,
        limit: 100 
      })
      
      if (response.data.success) {
        setOrders(response.data.orders)
      } else {
        toast.error(t("Failed to load orders"))
      }
    } catch (error) {
      toast.error(t("Failed to load orders"))
      console.error('Orders load error:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadBusinessInfo = async () => {
    setLoadingBusinessInfo(true)
    setBusinessInfo(await loadStoreBusinessInfo(getCurrency()))
    setLoadingBusinessInfo(false)
  }

  const applyFilters = () => {
    let filtered = [...orders]

    if (filters.status) {
      filtered = filtered.filter(order => order.status === filters.status)
    }

    if (filters.table) {
      filtered = filtered.filter(order => 
        order.table_number && order.table_number.toLowerCase().includes(filters.table.toLowerCase())
      )
    }

    if (filters.search) {
      filtered = filtered.filter(order => 
        order.order_code.toLowerCase().includes(filters.search.toLowerCase()) ||
        (order.customer_name && order.customer_name.toLowerCase().includes(filters.search.toLowerCase())) ||
        (order.pin && order.pin.includes(filters.search))
      )
    }

    setFilteredOrders(filtered)
  }

  const handleSearchByCodeOrPin = async (searchValue) => {
    if (!searchValue.trim()) {
      toast.error(t("Please enter an order code or PIN"))
      return
    }

    try {
      // Check if it's a PIN (8 digits) or order code
      const isPin = /^\d{8}$/.test(searchValue.trim());
      
      if (isPin) {
        // Search by PIN using the internal search endpoint (requires auth)
        const pinResponse = await ordersAPI.searchOrderByPin(searchValue.trim())
        if (pinResponse.data && pinResponse.data.order) {
          setSelectedOrder(pinResponse.data.order)
          setShowOrderModal(true)
          setSearchParams({ orderSearchQuery: searchValue.trim() })
          return
        }
      } else {
        // Search by order code
        const response = await ordersAPI.getOrderByCode(searchValue.trim())
        if (response.data && response.data.order) {
          setSelectedOrder(response.data.order)
          setShowOrderModal(true)
          setSearchParams({ orderSearchQuery: searchValue.trim() })
          return
        }
      }
      
      toast.error(t("Order not found with the provided code or PIN"))
    } catch (error) {
      toast.error(t("Order not found with the provided code or PIN"))
      console.error('Order search error:', error)
    }
  }

  const handlePaymentConfirmation = async (order) => {
    try {
      const newStatus = order.payment_status === 'PAID' ? 'UNPAID' : 'PAID'
      
      await ordersAPI.updatePayment(order.id, {
        paymentStatus: newStatus,
        paymentMethod: order.payment_method || 'cash'
      })

      // Update local state
      setOrders(prev => prev.map(o => 
        o.id === order.id 
          ? { ...o, payment_status: newStatus, status: newStatus === 'PAID' ? 'CONFIRMED' : o.status }
          : o
      ))

      // Update selected order in modal
      if (selectedOrder && selectedOrder.id === order.id) {
        setSelectedOrder(prev => ({
          ...prev,
          payment_status: newStatus,
          status: newStatus === 'PAID' ? 'CONFIRMED' : prev.status
        }))
      }

      if (newStatus === 'PAID') {
        // Send to kitchen via Socket.IO when payment is confirmed
        if (socket) {
          socket.emit('order.paid', {
            orderId: order.id,
            branchId: order.branch_id,
            orderCode: order.order_code
          })
        }
        toast.success(t("Payment confirmed! Order sent to kitchen"))
      } else {
        toast.success(t("Payment status updated"))
      }

      loadOrders()
    } catch (error) {
      toast.error(t("Failed to update payment status"))
      console.error('Payment update error:', error)
    }
  }

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateOrderStatus(orderId, newStatus)
      
      // Update local state
      setOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, status: newStatus } : o
      ))
      
      // Update selected order in modal
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({
          ...prev,
          status: newStatus
        }))
      }
      
      // Send status update via Socket.IO for real-time updates
      if (socketUpdateOrderStatus) {
        socketUpdateOrderStatus(orderId, newStatus)
      } else if (socket) {
        socket.emit('order.updated', { orderId, status: newStatus })
      }
      
      toast.success(t("Order status updated to {newStatus}", { newStatus: newStatus }))
      loadOrders()
    } catch (error) {
      toast.error(t("Failed to update order status"))
      console.error('Status update error:', error)
    }
  }

  const handlePrintInvoice = (order) => {
    setSelectedOrder(order)
    setShowInvoiceModal(true)
  }

  const closeModal = () => {
    setShowOrderModal(false)
    setSelectedOrder(null)
    // Remove search query from URL when closing modal
    setSearchParams({})
  }

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false)
    setSelectedOrder(null)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'CONFIRMED': return 'bg-blue-100 text-blue-800'
      case 'PREPARING': return 'bg-orange-100 text-orange-800'
      case 'READY': return 'bg-green-100 text-green-800'
      case 'SERVED': return 'bg-purple-100 text-purple-800'
      case 'COMPLETED': return 'bg-gray-100 text-gray-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-green-100 text-green-800'
      case 'PARTIAL': return 'bg-yellow-100 text-yellow-800'
      case 'UNPAID': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(locale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">{t("Loading orders...")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("Orders")}</h1>
          <p className="text-gray-600">{t("Manage customer orders and status")}</p>
        </div>
        <button
          onClick={loadOrders}
          className="btn-outline rounded-lg px-4 py-2 flex items-center gap-2"
        >
          {t("Refresh")}
        </button>
      </div>

      {/* Quick Search by Order Code or PIN */}
      <div className="card bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="card-body">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <QrCodeIcon className="h-5 w-5" />
            {t("Quick Order Search")}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t("Enter order code or PIN manually, or use a QR scanner device/app to scan the payment QR code")}
          </p>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute start-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchByCodeOrPin(searchInput)}
                placeholder={t("Enter order code or PIN (e.g., CAS-20251028-0002 or 69709299)")}
                className="form-input ps-10"
              />
            </div>
            <button
              onClick={() => handleSearchByCodeOrPin(searchInput)}
              className="btn-primary px-8 whitespace-nowrap rounded-lg flex items-center justify-center "
            >
              <MagnifyingGlassIcon className="h-5 w-5 me-2" />
              {t("Search Order")}
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
            <KeyIcon className="h-4 w-4" />
            <span>{t("You can search by order code (CAS-20251028-0002) or PIN (69709299)")}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label">{t("Filter by Code/Customer/PIN")}</label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute start-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder={t("Order code, customer name, or PIN")}
                  className="form-input ps-10"
                />
              </div>
            </div>

            <div>
              <label className="form-label">{t("Status")}</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="form-input"
              >
                <option value="">{t("All Statuses")}</option>
                <option value="PENDING">{t("Pending")}</option>
                <option value="CONFIRMED">{t("Confirmed")}</option>
                <option value="PREPARING">{t("Preparing")}</option>
                <option value="READY">{t("Ready")}</option>
                <option value="SERVED">{t("Served")}</option>
                <option value="COMPLETED">{t("Completed")}</option>
                <option value="CANCELLED">{t("Cancelled")}</option>
              </select>
            </div>

            <div>
              <label className="form-label">{isRestaurant ? t("Table") : t("Order reference")}</label>
              <input
                type="text"
                value={isRestaurant ? filters.table : filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, [isRestaurant ? 'table' : 'search']: e.target.value }))}
                placeholder={isRestaurant ? t("Table number") : t("Search orders")}
                className="form-input"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilters({ status: '', table: '', search: '' })}
                className="btn-outline w-full rounded-lg h-10"
              >
                {t("Clear Filters")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card">
        <div className="card-body">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">{t("No orders found")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("Order")}
                    </th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {isRestaurant ? t("Table") : t("Fulfilment")}
                    </th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("Customer")}
                    </th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("Total")}
                    </th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("Payment")}
                    </th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("Status")}
                    </th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("Time")}
                    </th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("Actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.order_code}</div>
                        <div className="text-sm text-gray-500">{t("PIN:")} {order.pin}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isRestaurant ? order.table_number || '-' : order.order_type === 'DELIVERY' ? t("Delivery") : t("Pickup")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.customer_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {money(order.total)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handlePaymentConfirmation(order)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            order.payment_status === 'PAID'
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                          title={t("Click to toggle payment status")}
                        >
                          {order.payment_status === 'PAID' ? (
                            <>
                              <CheckCircleIcon className="h-3 w-3 me-1" />
                              {t("PAID")}
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-3 w-3 me-1" />
                              {t("UNPAID")}
                            </>
                          )}
                        </button>
                        <div className="mt-1 text-xs text-gray-500">{t(PAYMENT_METHOD_LABELS[order.payment_method] || "Cash")}</div>
                        {PROOF_BADGES[order.payment_proof_status] && order.payment_status !== 'PAID' && (
                          <button onClick={() => { setSelectedOrder(order); setShowOrderModal(true) }} className={`mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${PROOF_BADGES[order.payment_proof_status][1]}`}>
                            {PROOF_BADGES[order.payment_proof_status][0]}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                          className={`text-xs font-medium rounded-full px-3 py-1 border-0 ${getStatusColor(order.status)}`}
                        >
                          <option value="PENDING">{t("PENDING")}</option>
                          <option value="CONFIRMED">{t("CONFIRMED")}</option>
                          <option value="PREPARING">{t("PREPARING")}</option>
                          <option value="READY">{t("READY")}</option>
                          <option value="SERVED">{t("SERVED")}</option>
                          <option value="COMPLETED">{t("COMPLETED")}</option>
                          <option value="CANCELLED">{t("CANCELLED")}</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleTimeString(locale())}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => {
                            setSelectedOrder(order)
                            setShowOrderModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title={t("View Order Details")}
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-8">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{t("Order Details")}</h2>
                  <p className="text-gray-600">{t("Order #")}{selectedOrder.order_code}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Order Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">{t("Customer Name")}</p>
                  <p className="font-semibold text-lg">{selectedOrder.customer_name || t("No Name")}</p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">{isRestaurant ? t("Table Number") : t("Fulfilment")}</p>
                  <p className="font-semibold text-lg">{isRestaurant ? selectedOrder.table_number || t("No Table") : selectedOrder.order_type === 'DELIVERY' ? t("Delivery") : t("Pickup")}</p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">{t("Order PIN")}</p>
                  <p className="font-semibold text-lg font-mono">{selectedOrder.pin || t("No PIN")}</p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">{t("Branch")}</p>
                  <p className="font-semibold">{selectedOrder.branch_name || t("Unknown Branch")}</p>
                </div>
              </div>

              {/* Status and Payment Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">{t("Payment Status")}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`badge ${getPaymentStatusColor(selectedOrder.payment_status)} text-lg`}>
                      {selectedOrder.payment_status}
                    </span>
                    <span className="text-sm text-gray-600">
                      ({t(PAYMENT_METHOD_LABELS[selectedOrder.payment_method] || "Cash")})
                    </span>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">{t("Order Status")}</p>
                  <div className="mt-1">
                    <select
                      value={selectedOrder.status}
                      onChange={(e) => handleStatusUpdate(selectedOrder.id, e.target.value)}
                      className={`text-sm font-medium rounded-full px-4 py-2 border-0 w-full ${getStatusColor(selectedOrder.status)}`}
                    >
                      <option value="PENDING">{t("PENDING")}</option>
                      <option value="CONFIRMED">{t("CONFIRMED")}</option>
                      <option value="PREPARING">{t("PREPARING")}</option>
                      <option value="READY">{t("READY")}</option>
                      <option value="SERVED">{t("SERVED")}</option>
                      <option value="COMPLETED">{t("COMPLETED")}</option>
                      <option value="CANCELLED">{t("CANCELLED")}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">{t("Order Created")}</p>
                  <p className="font-semibold">{formatDate(selectedOrder.created_at)}</p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">{t("Last Updated")}</p>
                  <p className="font-semibold">{formatDate(selectedOrder.updated_at)}</p>
                </div>
              </div>

              <PaymentProofReview
                order={selectedOrder}
                onChange={(updated) => {
                  setSelectedOrder(updated)
                  setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o))
                }}
              />

              {selectedOrder.order_type === 'DELIVERY' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                  <h3 className="font-bold text-gray-900 mb-2 text-lg">{t("Delivery")}</h3>
                  {selectedOrder.shipping_area && <p className="text-sm text-gray-700"><span className="font-medium">{t("Area")}:</span> {selectedOrder.shipping_area}</p>}
                  {selectedOrder.delivery_address && <p className="text-sm text-gray-700 whitespace-pre-line mt-1"><span className="font-medium">{t("Address")}:</span> {selectedOrder.delivery_address}</p>}
                  {selectedOrder.customer_phone && <p className="text-sm text-gray-700 mt-1"><span className="font-medium">{t("Phone")}:</span> <a className="text-blue-600" href={`tel:${selectedOrder.customer_phone}`} dir="ltr">{selectedOrder.customer_phone}</a></p>}
                  <p className="text-sm text-gray-700 mt-1"><span className="font-medium">{t("Delivery fee")}:</span> {Number(selectedOrder.shipping_fee) > 0 ? money(selectedOrder.shipping_fee) : t("Free")}</p>
                </div>
              )}

              {/* Financial Breakdown */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-lg">{t("Financial Breakdown")}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t("Subtotal")}</p>
                    <p className="text-lg font-semibold">{money(Number(selectedOrder.total || 0) - Number(selectedOrder.tax || 0) - Number(selectedOrder.service_charge || 0) - Number(selectedOrder.shipping_fee || 0))}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t("Tax")}</p>
                    <p className="text-lg font-semibold">{money(selectedOrder.tax)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t("Service Charge")}</p>
                    <p className="text-lg font-semibold">{money(selectedOrder.service_charge)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t("Total Amount")}</p>
                    <p className="text-2xl font-bold text-blue-600">{money(selectedOrder.total)}</p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-4 text-lg">{t("Order Items")}</h3>
                  <div className="space-y-3">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {item.menu_item_name || item.item_name || t("Item {v0}", { v0: index + 1 })}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {t("Quantity:")} {item.quantity} × {money(item.unit_price)}
                            </p>
                            {item.note && (
                              <p className="text-sm text-gray-500 mt-1">
                                {t("Note:")} {item.note}
                              </p>
                            )}
                            {item.modifiers && item.modifiers.length > 0 && (
                              <div className="mt-2">
                                <p className="text-sm text-gray-600 font-medium">{t("Modifiers:")}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {item.modifiers.map((modifier, modIndex) => (
                                    <span key={modIndex} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                      {modifier.name} (+{money(modifier.extra_price)})
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="text-end">
                            <p className="text-lg font-bold text-gray-900">
                              {money((Number(item.unit_price || 0) + (item.modifiers?.reduce((sum, mod) => sum + Number(mod.extra_price || 0), 0) || 0)) * Number(item.quantity || 0))}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => handlePaymentConfirmation(selectedOrder)}
                  className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-colors ${
                    selectedOrder.payment_status === 'PAID'
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {selectedOrder.payment_status === 'PAID' ? t("Mark as Unpaid") : t("Confirm Payment")}
                </button>
                
                <button
                  onClick={() => handlePrintInvoice(selectedOrder)}
                  className="flex items-center justify-center gap-2 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
                >
                  <PrinterIcon className="h-5 w-5" />
                  {t("Print Invoice")}
                </button>
                
                <button
                  onClick={closeModal}
                  className="flex-1 py-3 px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
                >
                  {t("Close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && selectedOrder && businessInfo && (
        <InvoiceRenderer
          order={selectedOrder}
          template={selectedTemplate}
          businessInfo={businessInfo}
          onClose={closeInvoiceModal}
        />
      )}
    </div>
  )
}

export default OrdersPage
